import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs, { Dayjs } from "dayjs";

interface CustomDateRangePickerProps {
  onDateRangeChange: (from: Dayjs | null, to: Dayjs | null) => void;
  startDateError?: string;
  endDateError?: string;
  setFromDate: (date: Dayjs | null) => void;
  setToDate: (date: Dayjs | null) => void;
  fromDate?: Dayjs | null;
  toDate?: Dayjs | null;
  onFromDateError?: (error: string | null) => void;
  onToDateError?: (error: string | null) => void;
}

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    height: "44px",
    fontSize: "14px",
    borderColor: "#D1D5DC",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
  },
};

const getDateErrorMessage = (
  reason: string | null,
  fallbackMessage: string,
) => {
  if (!reason) return null;

  if (reason === "disableFuture" || reason === "maxDate") {
    return "Future date not allowed";
  }

  return fallbackMessage;
};

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  onDateRangeChange,
  startDateError,
  endDateError,
  setFromDate,
  setToDate,
  fromDate,
  toDate,
  onFromDateError,
  onToDateError,
  allowFutureDates = false,
}) => {
  const [fromDateLocal, setFromDateLocal] = useState<Dayjs | null>(fromDate ?? null);
  const [toDateLocal, setToDateLocal] = useState<Dayjs | null>(toDate ?? null);

  useEffect(() => {
    setFromDateLocal(fromDate ?? null);
    setToDateLocal(toDate ?? null);
  }, [fromDate, toDate]);

  const handleFromDateChange = (newValue: Dayjs | null) => {
    setFromDateLocal(newValue);
    setFromDate(newValue);
    onDateRangeChange(newValue, toDateLocal);
  };

  const handleToDateChange = (newValue: Dayjs | null) => {
    setToDateLocal(newValue);
    setToDate(newValue);
    onDateRangeChange(fromDateLocal, newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" gap={2} mt={2} flexDirection="column">
        {/* FROM DATE */}
        <Box flex={1} className="date-picker-wrapper">
          <Typography fontSize="14px" mb={0.5} sx={{ color: "#6A7282"}}>
            From
          </Typography>
          <DesktopDatePicker
            value={fromDateLocal}
            onChange={handleFromDateChange}
            maxDate={toDateLocal ?? (allowFutureDates ? undefined : dayjs())}
            disableFuture={!allowFutureDates}
            onError={(reason) => {
              onFromDateError?.(
                getDateErrorMessage(reason, "Invalid start date"),
              );
            }}
            slotProps={{
              textField: {
                placeholder: "Enter date",
                fullWidth: true,
                size: "small",
                sx: inputStyles,
              },
            }}
          />
          {startDateError && (
            <Typography color="error" fontSize="12px" mt={0.5}>
              {startDateError}
            </Typography>
          )}
        </Box>

        {/* TO DATE */}
        <Box flex={1} className="date-picker-wrapper">
          <Typography fontSize="14px" mb={0.5} sx={{ color: "#6A7282" }}>
            To
          </Typography>
          <DesktopDatePicker
            value={toDateLocal}
            onChange={handleToDateChange}
            minDate={fromDateLocal ?? undefined}
            maxDate={allowFutureDates ? undefined : dayjs()}
            disableFuture={!allowFutureDates}
            onError={(reason) => {
              onToDateError?.(getDateErrorMessage(reason, "Invalid end date"));
            }}
            slotProps={{
              textField: {
                placeholder: "Enter date",
                fullWidth: true,
                size: "small",
                sx: inputStyles,
              },
            }}
          />
          {endDateError && (
            <Typography color="error" fontSize="12px" mt={0.5}>
              {endDateError}
            </Typography>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default CustomDateRangePicker;
